/**
 * 知识库API服务
 *
 * 封装知识库相关的HTTP REST API调用，包括：
 * - 手动同步知识库（使用轮询机制）
 * - 获取同步日志
 * - 获取向量知识条目列表
 * - 获取知识图谱可视化数据
 */

import type {
  VectorListRequest,
  VectorListResponse,
  GraphDataRequest,
  GraphData,
  SyncManualRequest,
  SyncLogsResponse,
  SyncProgressResponse,
  SyncCompletedResponse,
  EpisodeBuildTask,
  EpisodeBuildStartResponse,
} from '../types/knowledge';

// API基础URL配置
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// HTTP错误类（复用 documentApi 的错误类）
export class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export class UnauthorizedError extends HttpError {
  constructor(message: string = '未授权，请重新登录') {
    super(message, 401, 'UNAUTHORIZED');
    this.name = 'UnauthorizedError';
  }
}

export class ForbiddenError extends HttpError {
  constructor(message: string = '没有权限执行此操作') {
    super(message, 403, 'FORBIDDEN');
    this.name = 'ForbiddenError';
  }
}

export class NotFoundError extends HttpError {
  constructor(message: string = '资源不存在') {
    super(message, 404, 'NOT_FOUND');
    this.name = 'NotFoundError';
  }
}

export class ValidationError extends HttpError {
  constructor(message: string = '数据验证失败') {
    super(message, 400, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
  }
}

/**
 * 获取请求头
 *
 * @param token - 可选的认证令牌，如果提供则使用 Header 认证，否则使用 Cookie 认证
 */
function getHeaders(token: string | undefined): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * 处理HTTP响应错误
 */
async function handleResponseError(response: Response): Promise<never> {
  let errorMessage = '请求失败';

  try {
    const data = await response.json();
    errorMessage = data.detail || data.message || errorMessage;
  } catch {
    // 无法解析JSON，使用默认错误消息
  }

  switch (response.status) {
    case 401:
      throw new UnauthorizedError(errorMessage);
    case 403:
      throw new ForbiddenError(errorMessage);
    case 404:
      throw new NotFoundError(errorMessage);
    case 400:
      throw new ValidationError(errorMessage);
    default:
      throw new HttpError(errorMessage, response.status);
  }
}

/**
 * 知识库服务类
 *
 * 提供知识库管理的所有前端API方法
 */
export class KnowledgeService {
  private static readonly DEFAULT_TIMEOUT = 30000; // 30秒默认超时
  private static readonly SYNC_TIMEOUT = 300000; // 5分钟同步超时
  private static readonly POLL_INTERVAL = 2000; // 2秒轮询间隔
  private static readonly MAX_POLL_ATTEMPTS = 150; // 最大轮询次数（5分钟）

  /**
   * 验证工作区ID
   * @throws Error 如果工作区ID无效
   */
  private static validateWorkspaceId(workspaceId: number): void {
    if (!workspaceId || typeof workspaceId !== 'number' || workspaceId <= 0) {
      throw new Error('无效的工作区ID');
    }
  }

  /**
   * 安全执行异步操作并处理错误
   */
  private static async safeExecute<T>(
    operation: () => Promise<T>,
    errorContext: string
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      const message = error instanceof Error
        ? KnowledgeService.sanitizeErrorMessage(error.message, errorContext)
        : `${errorContext}时发生未知错误`;
      console.error(`[KnowledgeService] ${errorContext}:`, error);
      throw new Error(message);
    }
  }

  /**
   * 脱敏错误信息，防止泄露系统敏感信息
   */
  private static sanitizeErrorMessage(originalMessage: string, context: string): string {
    const sensitivePatterns = [
      /password/i, /token/i, /secret/i, /api[_-]?key/i, /database/i,
      /sql/i, /query/i, /table/i, /column/i, /schema/i,
      /connection\s*string/i, /credentials?/i, /authorization/i, /auth/i
    ];

    const hasSensitiveInfo = sensitivePatterns.some(pattern => pattern.test(originalMessage));
    return hasSensitiveInfo ? `${context}失败，请联系管理员` : originalMessage;
  }

  /**
   * 轮询查询同步进度
   * @param workspaceId - 工作区ID
   * @param syncId - 同步任务ID
   * @param token - 可选的认证令牌
   * @returns 同步进度响应
   */
  private async pollSyncProgress(
    workspaceId: number,
    syncId: string,
    token?: string
  ): Promise<SyncProgressResponse> {
    const response = await fetch(
      `${API_BASE_URL}/api/knowledge/workspaces/${workspaceId}/sync/${syncId}`,
      {
        method: 'GET',
        headers: getHeaders(token),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 手动同步知识库（使用轮询机制）
   *
   * @param workspaceId - 工作区ID
   * @param options - 同步选项
   * @param onProgress - 进度回调函数（可选）
   * @param token - 可选的认证令牌
   * @returns 同步结果
   * @throws Error 如果同步失败
   *
   * @example
   * ```typescript
   * const result = await knowledgeService.manualSync(
   *   1,
   *   { syncChapters: true, syncSettings: true },
   *   (progress) => {
   *     console.log(`同步进度: ${progress.status}`);
   *   }
   * );
   * console.log(`同步完成，共同步 ${result.chapters_synced} 个章节`);
   * ```
   */
  async manualSync(
    workspaceId: number,
    options: SyncManualRequest = {},
    onProgress?: (progress: SyncProgressResponse) => void,
    token?: string
  ): Promise<SyncCompletedResponse> {
    return KnowledgeService.safeExecute(async () => {
      KnowledgeService.validateWorkspaceId(workspaceId);

      // 1. 启动同步任务
      const startResponse = await fetch(
        `${API_BASE_URL}/api/knowledge/workspaces/${workspaceId}/sync`,
        {
          method: 'POST',
          headers: getHeaders(token),
          credentials: 'include',
          body: JSON.stringify(options),
        }
      );

      if (!startResponse.ok) {
        await handleResponseError(startResponse);
      }

      const startData = await startResponse.json();
      const syncId = startData.sync_id;

      console.log('[KnowledgeService.manualSync] 同步已启动:', { syncId, workspaceId });

      // 2. 轮询查询同步进度
      let attempts = 0;
      const startTime = Date.now();

      while (attempts < KnowledgeService.MAX_POLL_ATTEMPTS) {
        // 检查超时
        if (Date.now() - startTime > KnowledgeService.SYNC_TIMEOUT) {
          throw new Error('同步超时');
        }

        // 等待轮询间隔
        await new Promise(resolve => setTimeout(resolve, KnowledgeService.POLL_INTERVAL));

        // 查询进度
        const progress = await this.pollSyncProgress(workspaceId, syncId, token);

        // 触发进度回调
        if (onProgress) {
          onProgress(progress);
        }

        // 检查同步状态
        if (progress.status === 'success') {
          console.log('[KnowledgeService.manualSync] 同步完成:', progress);
          return {
            sync_id: progress.sync_id,
            chapters_synced: progress.chapters_synced || 0,
            settings_synced: progress.settings_synced || 0,
            duration_ms: progress.duration_ms || 0,
            completed_at: progress.created_at,
          };
        }

        if (progress.status === 'failed') {
          const errorMsg = progress.error_message || '同步失败';
          console.error('[KnowledgeService.manualSync] 同步失败:', errorMsg);
          throw new Error(errorMsg);
        }

        // 继续轮询
        attempts++;
      }

      throw new Error('同步超时（超过最大轮询次数）');

    }, '手动同步');
  }

  /**
   * 获取同步日志
   * @param workspaceId - 工作区ID
   * @param page - 页码（默认1）
   * @param pageSize - 每页大小（默认20）
   * @param token - 可选的认证令牌
   * @returns 分页的同步日志
   */
  async getSyncLogs(
    workspaceId: number,
    page: number = 1,
    pageSize: number = 20,
    token?: string
  ): Promise<SyncLogsResponse> {
    return KnowledgeService.safeExecute(async () => {
      KnowledgeService.validateWorkspaceId(workspaceId);

      if (page < 1 || pageSize < 1 || pageSize > 100) {
        throw new Error('无效的分页参数');
      }

      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      const response = await fetch(
        `${API_BASE_URL}/api/knowledge/workspaces/${workspaceId}/sync-logs?${params.toString()}`,
        {
          method: 'GET',
          headers: getHeaders(token),
          credentials: 'include',
        }
      );

      if (!response.ok) {
        await handleResponseError(response);
      }

      return await response.json();
    }, '获取同步日志');
  }

  /**
   * 获取向量知识条目列表
   * @param workspaceId - 工作区ID
   * @param filters - 过滤条件（可选）
   * @param token - 可选的认证令牌
   * @returns 分页的向量知识条目列表
   */
  async getVectorsList(
    workspaceId: number,
    filters: VectorListRequest = {},
    token?: string
  ): Promise<VectorListResponse> {
    return KnowledgeService.safeExecute(async () => {
      KnowledgeService.validateWorkspaceId(workspaceId);

      const params = new URLSearchParams();
      if (filters.type) {
        params.append('type', filters.type);
      }
      if (filters.page) {
        params.append('page', filters.page.toString());
      }
      if (filters.page_size) {
        params.append('page_size', filters.page_size.toString());
      }

      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/knowledge/workspaces/${workspaceId}/vectors${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(token),
        credentials: 'include',
      });

      if (!response.ok) {
        await handleResponseError(response);
      }

      return await response.json();
    }, '获取向量列表');
  }

  /**
   * 获取知识图谱可视化数据
   * @param workspaceId - 工作区ID
   * @param options - 查询选项（可选）
   * @param token - 可选的认证令牌
   * @returns 知识图谱数据（节点和边）
   */
  async getGraphData(
    workspaceId: number,
    options: GraphDataRequest = {},
    token?: string
  ): Promise<GraphData> {
    return KnowledgeService.safeExecute(async () => {
      KnowledgeService.validateWorkspaceId(workspaceId);

      const params = new URLSearchParams();
      if (options.limit !== undefined) {
        params.append('limit', options.limit.toString());
      }
      if (options.node_types && options.node_types.length > 0) {
        params.append('node_types', options.node_types.join(','));
      }
      if (options.chapter_num !== undefined) {
        params.append('chapter_num', options.chapter_num.toString());
      }

      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/knowledge/workspaces/${workspaceId}/graph${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(token),
        credentials: 'include',
      });

      if (!response.ok) {
        await handleResponseError(response);
      }

      const data = await response.json();

      // 转换为前端使用的格式，保留完整的后端数据（通过 _cytoscapeData）
      const nodes = (data.nodes || []).map((node: { data: Record<string, unknown>; classes?: string }) => ({
        id: node.data.id as string,
        label: (node.data.label as string) || '',
        type: node.data.type as string,
        workspace_id: node.data.workspace_id as number,
        name: (node.data.label as string) || (node.data.name as string) || '',
        classes: node.classes,
        // 保留后端返回的完整 data 对象，避免丢失 bg, border, summary, mention_count, valid_at, invalid_at 等字段
        _cytoscapeData: node.data,
      }));

      const edges = (data.edges || []).map((edge: { data: Record<string, unknown> }) => ({
        id: `${edge.data.source}-${edge.data.target}`,
        source: edge.data.source as string,
        target: edge.data.target as string,
        label: edge.data.label as string || '',
        fact: edge.data.fact as string | undefined,
        valid_at: edge.data.valid_at as string | undefined,
        invalid_at: edge.data.invalid_at as string | undefined,
      }));

      return {
        nodes,
        edges,
        total: data.stats?.node_count || nodes.length,
      };
    }, '获取图谱数据');
  }

  /**
   * 获取设定相关的知识图谱数据
   * @param workspaceId - 工作区ID
   * @param settingId - 设定ID
   * @param depth - 关系扩展深度（默认2）
   * @param token - 可选的认证令牌
   * @returns 设定相关的知识图谱数据
   */
  async getSettingRelatedGraph(
    workspaceId: number,
    settingId: number,
    depth: number = 2,
    token?: string
  ): Promise<GraphData> {
    return KnowledgeService.safeExecute(async () => {
      KnowledgeService.validateWorkspaceId(workspaceId);

      if (!settingId || typeof settingId !== 'number' || settingId <= 0) {
        throw new Error('无效的设定ID');
      }

      const params = new URLSearchParams();
      params.append('depth', depth.toString());

      const url = `${API_BASE_URL}/api/knowledge/workspaces/${workspaceId}/graph/setting/${settingId}?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(token),
        credentials: 'include',
      });

      if (!response.ok) {
        await handleResponseError(response);
      }

      const data = await response.json();

      const nodes = (data.nodes || []).map((node: { data: Record<string, unknown>; classes?: string }) => ({
        id: node.data.id as string,
        label: (node.data.label as string) || '',
        type: node.data.type as string,
        workspace_id: node.data.workspace_id as number,
        name: (node.data.label as string) || (node.data.name as string) || '',
        classes: node.classes,
        _cytoscapeData: node.data,
      }));

      const edges = (data.edges || []).map((edge: { data: Record<string, unknown> }) => ({
        id: `${edge.data.source}-${edge.data.target}`,
        source: edge.data.source as string,
        target: edge.data.target as string,
        label: edge.data.label as string || '',
      }));

      return {
        nodes,
        edges,
        total: data.stats?.node_count || nodes.length,
      };
    }, '获取设定相关图谱数据');
  }

  /**
   * 检查章节或设定的知识图谱是否已构建
   * @param workspaceId - 工作区ID
   * @param chapterId - 章节ID（可选）
   * @param settingId - 设定ID（可选）
   * @param token - 可选的认证令牌
   * @returns Episode 状态信息
   */
  async checkEpisodeStatus(
    workspaceId: number,
    chapterId?: number,
    settingId?: number,
    token?: string
  ): Promise<{ exists: boolean; entity_count?: number; edge_count?: number; error?: string }> {
    return KnowledgeService.safeExecute(async () => {
      KnowledgeService.validateWorkspaceId(workspaceId);

      if (!chapterId && !settingId) {
        throw new Error('必须提供 chapterId 或 settingId');
      }

      const params = new URLSearchParams();
      if (chapterId) {
        params.append('chapter_id', chapterId.toString());
      }
      if (settingId) {
        params.append('setting_id', settingId.toString());
      }

      const url = `${API_BASE_URL}/api/knowledge/workspaces/${workspaceId}/episode/check?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(token),
        credentials: 'include',
      });

      if (!response.ok) {
        await handleResponseError(response);
      }

      return await response.json();
    }, '检查 Episode 状态');
  }

  /**
   * 构建章节或设定的知识图谱
   * @param workspaceId - 工作区ID
   * @param chapterId - 章节ID（可选）
   * @param settingId - 设定ID（可选）
   * @param token - 可选的认证令牌
   * @returns 构建结果
   */
  async buildEpisode(
    workspaceId: number,
    chapterId?: number,
    settingId?: number,
    token?: string
  ): Promise<{ success: boolean; entity_count?: number; edge_count?: number; duration_ms?: number; error?: string }> {
    return KnowledgeService.safeExecute(async () => {
      KnowledgeService.validateWorkspaceId(workspaceId);

      if (!chapterId && !settingId) {
        throw new Error('必须提供 chapterId 或 settingId');
      }

      const params = new URLSearchParams();
      if (chapterId) {
        params.append('chapter_id', chapterId.toString());
      }
      if (settingId) {
        params.append('setting_id', settingId.toString());
      }

      const url = `${API_BASE_URL}/api/knowledge/workspaces/${workspaceId}/episode/build?${params.toString()}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(token),
        credentials: 'include',
      });

      if (!response.ok) {
        await handleResponseError(response);
      }

      return await response.json();
    }, '构建知识图谱');
  }

  async startEpisodeBuild(
    workspaceId: number,
    chapterId?: number,
    settingId?: number,
    token?: string
  ): Promise<EpisodeBuildStartResponse> {
    return KnowledgeService.safeExecute(async () => {
      KnowledgeService.validateWorkspaceId(workspaceId);

      if (!chapterId && !settingId) {
        throw new Error('必须提供 chapterId 或 settingId');
      }

      const params = new URLSearchParams();
      if (chapterId) {
        params.append('chapter_id', chapterId.toString());
      }
      if (settingId) {
        params.append('setting_id', settingId.toString());
      }

      const url = `${API_BASE_URL}/api/knowledge/workspaces/${workspaceId}/episode/build?${params.toString()}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(token),
        credentials: 'include',
      });

      if (!response.ok) {
        await handleResponseError(response);
      }

      return await response.json();
    }, '启动知识图谱构建');
  }

  async getEpisodeBuildProgress(
    workspaceId: number,
    taskId: string,
    token?: string
  ): Promise<EpisodeBuildTask> {
    return KnowledgeService.safeExecute(async () => {
      KnowledgeService.validateWorkspaceId(workspaceId);

      if (!taskId) {
        throw new Error('必须提供 taskId');
      }

      const url = `${API_BASE_URL}/api/knowledge/workspaces/${workspaceId}/episode/build/${taskId}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(token),
        credentials: 'include',
      });

      if (!response.ok) {
        await handleResponseError(response);
      }

      return await response.json();
    }, '查询构建进度');
  }

  async translateEdgeLabels(
    workspaceId: number,
    token?: string
  ): Promise<{ success: boolean; translated_count?: number; error?: string }> {
    return KnowledgeService.safeExecute(async () => {
      KnowledgeService.validateWorkspaceId(workspaceId);

      const url = `${API_BASE_URL}/api/knowledge/workspaces/${workspaceId}/edges/translate`;

      const response = await fetch(url, {
        method: 'POST',
        headers: getHeaders(token),
        credentials: 'include',
      });

      if (!response.ok) {
        await handleResponseError(response);
      }

      return await response.json();
    }, '翻译边标签');
  }
}

/**
 * 知识库服务单例
 *
 * 使用单例模式确保全局只有一个服务实例
 */
export const knowledgeService = new KnowledgeService();

export default knowledgeService;
